import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { AnalysisFormProps } from '@modules/analysis/types';
import { CanAccess } from '@modules/common/components/CanAccess';
import { SaveArtefactConfirmDialog } from '@modules/common/components/SaveArtefact/SaveArtefactConfirmDialog';
import { SaveArtefactFunction } from '@modules/common/types';
import BookmarkRoundedIcon from '@mui/icons-material/BookmarkRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LoadingButton } from '@mui/lab';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  Skeleton,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  AnalysisUseCase,
  PermissionOperation,
  PermissionResource,
} from 'generated/graphql';
import { useState } from 'react';
import { useToggle } from 'react-use';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { Scope } from 'v2/core/entities/scope';
import { shallow } from 'zustand/shallow';
import { SaveAnalysisForm } from '../SaveAnalysisForm';

const trackSaveAsNewCTAEvents = createAnalyticsTracker('saveAsNewCTA');
const BUTTON_WIDTH = 115;

interface CreateOrUpdateAnalysisButtonProps {
  isDisabled?: boolean;
  isLoading?: boolean;
  onAnalysisUpdate: () => void;
  onAnalysisCreate: SaveArtefactFunction;
  analysisFormProps?: AnalysisFormProps;
}

export const CreateOrUpdateAnalysisButton = ({
  isDisabled,
  isLoading,
  onAnalysisUpdate,
  onAnalysisCreate,
  analysisFormProps,
}: CreateOrUpdateAnalysisButtonProps) => {
  const [popoverAnchorEl, setPopoverAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [isSaveAnalysisFormOpen, setIsSaveAnalysisFormOpen] = useToggle(false);
  const [savedAnalysisRoute, setSavedAnalysisRoute] = useState<null | string>(
    null,
  );
  const isPopoverOpen = Boolean(popoverAnchorEl);
  const {
    title,
    viewToken,
    useCase,
    isLoading: isAnalysisLoading,
  } = useAnalysisStore(
    ({ title, viewToken, useCase, isLoading }) => ({
      title,
      viewToken,
      useCase,
      isLoading,
    }),
    shallow,
  );

  const openAnalysisForm = () => {
    closePopover();
    setIsSaveAnalysisFormOpen(true);
    trackSaveAsNewCTAEvents({
      eventName:
        useCase === AnalysisUseCase.Compare
          ? 'Save As New CTA Clicked - Compare page'
          : 'Save As New CTA Clicked - Quantify page',
      artefactName: title,
      artefactViewToken: viewToken,
      isUserArtefactOwner: true,
    });
  };

  const closeAnalysisForm = () => setIsSaveAnalysisFormOpen(false);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setPopoverAnchorEl(event.currentTarget);
  };

  const closePopover = () => setPopoverAnchorEl(null);

  const onUpdateAnalysis = () => {
    closePopover();
    onAnalysisUpdate();
  };

  const onSubmitAnalysisForm = async (name: string, scope: Scope) => {
    const analysisDetails = await onAnalysisCreate(name, scope);
    setSavedAnalysisRoute(analysisDetails.artefactRoute);

    return analysisDetails;
  };

  const renderUpdateAnalysisButton = () => {
    const updateButton = (
      <LoadingButton
        variant="outlined"
        onClick={handlePopoverOpen}
        loading={isLoading}
        disabled={isDisabled || isLoading}
        loadingPosition="start"
        startIcon={<BookmarkRoundedIcon />}
        endIcon={<ExpandMoreIcon />}
      >
        Save
      </LoadingButton>
    );

    if (isDisabled) {
      return (
        <Tooltip title="There are no changes to save" placement="top" arrow>
          <Stack>{updateButton}</Stack>
        </Tooltip>
      );
    }
    return updateButton;
  };

  const popoverID = isPopoverOpen ? 'simple-popover' : undefined;

  return (
    <>
      <Stack>
        <CanAccess
          resource={PermissionResource.Artefact}
          operation={PermissionOperation.Write}
        >
          {isAnalysisLoading ? (
            <Skeleton variant="rectangular" width={BUTTON_WIDTH} height={40} />
          ) : (
            renderUpdateAnalysisButton()
          )}
        </CanAccess>
      </Stack>
      <Popover
        id={popoverID}
        open={isPopoverOpen}
        anchorEl={popoverAnchorEl}
        onClose={closePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <List>
          <ListItem disablePadding>
            <ListItemButton sx={{ py: 0.5 }} onClick={onUpdateAnalysis}>
              <ListItemText primary="Update" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton sx={{ py: 0.5 }} onClick={openAnalysisForm}>
              <ListItemText primary="Save As New" />
            </ListItemButton>
          </ListItem>
        </List>
      </Popover>
      <SaveArtefactConfirmDialog
        artefactRoute={savedAnalysisRoute}
        fullWidth
        maxWidth="sm"
        PaperProps={{ elevation: 3 }}
        open={isSaveAnalysisFormOpen}
        onClose={closeAnalysisForm}
      >
        <SaveAnalysisForm
          headerText={
            useCase === AnalysisUseCase.Compare
              ? 'Save New Compare'
              : 'Save New Quantify'
          }
          buttonText="Save as new"
          onSubmit={onSubmitAnalysisForm}
          onClose={closeAnalysisForm}
          useCase={useCase}
          analysisFormProps={analysisFormProps}
        />
      </SaveArtefactConfirmDialog>
    </>
  );
};
